import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import FormProvider from 'src/components/hook-form/form-provider';
import { RHFAutocomplete, RHFSelect, RHFTextField } from 'src/components/hook-form';
import LoadingButton from '@mui/lab/LoadingButton';
import RHFRadioGroupLegacy from 'src/components/hook-form/rhf-radio-group-legacy';
import { routes } from 'src/components/upload/SingleFilePreview';
import { RHFUploadSingleFileAccelerator } from 'src/components/hook-form/rhf-upload';
import { useMyDataStore } from 'src/store/me/me.store';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';

type Props = {
  hook: any;
};

type SectionKey = 'info' | 'display' | 'images';

type FloatingActionsRect = {
  left: number;
  width: number;
};

function SectionTitle({ index, title }: { index: number; title: string }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: 'common.white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
        }}
      >
        {index}
      </Box>
      <Typography variant="h6" fontWeight={700}>
        {title}
      </Typography>
    </Stack>
  );
}

type SectionCardProps = {
  section: SectionKey;
  index: number;
  title: string;
  expandedSections: Record<SectionKey, boolean>;
  onToggleSection: (section: SectionKey) => void;
  children: ReactNode;
};

function SectionCard({
  section,
  index,
  title,
  expandedSections,
  onToggleSection,
  children,
}: SectionCardProps) {
  const expanded = expandedSections[section];

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <SectionTitle index={index} title={title} />
          <IconButton onClick={() => onToggleSection(section)}>
            {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </Stack>

        <Collapse in={expanded} timeout="auto" unmountOnExit={false}>
          <Box sx={{ pt: 1 }}>{children}</Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}

function LayoutSlot({ label, sx }: { label: string; sx?: object }) {
  return (
    <Box
      sx={{
        border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.24)}`,
        borderRadius: 1,
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
        color: 'primary.main',
        display: 'grid',
        placeItems: 'center',
        fontWeight: 800,
        boxShadow: (theme) => `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.42)}`,
        ...sx,
      }}
    >
      {label}
    </Box>
  );
}

function BannerLayoutPreview({ type }: { type: string }) {
  if (type === 'two-images') {
    return (
      <Stack direction="row" spacing={0.75} sx={{ width: 320, maxWidth: 1 }}>
        <LayoutSlot label="1" sx={{ width: 1, height: 180 }} />
        <LayoutSlot label="2" sx={{ width: 1, height: 180 }} />
      </Stack>
    );
  }

  if (type === 'three-images') {
    return (
      <Stack direction="row" spacing={0.75} sx={{ width: 320, maxWidth: 1 }}>
        <LayoutSlot label="1" sx={{ width: 1, height: 180 }} />
        <Stack spacing={0.75} sx={{ width: 1 }}>
          <LayoutSlot label="2" sx={{ height: 86 }} />
          <LayoutSlot label="3" sx={{ height: 86 }} />
        </Stack>
      </Stack>
    );
  }

  return <LayoutSlot label="1" sx={{ width: 320, maxWidth: 1, height: 180 }} />;
}

export default function BannerForm({ hook }: Props) {
  const permissions = useMyDataStore(
    (state) => state.me?.cnAdminInfo?.permissions?.personalization
  );
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [floatingActionsRect, setFloatingActionsRect] = useState<FloatingActionsRect>({
    left: 0,
    width: 0,
  });
  const [expandedSections, setExpandedSections] = useState<Record<SectionKey, boolean>>({
    info: true,
    display: true,
    images: true,
  });
  const shouldHideNewUsersOption =
    (hook?.selectedBenefits?.length ?? 0) === 0 && (hook?.benefitsForNewUsers?.length ?? 0) === 0;
  const redirectOptions = shouldHideNewUsersOption
    ? routes.filter((option: any) => !option?.url?.includes('forNewUsers'))
    : routes;

  useEffect(() => {
    const updateFloatingActionsRect = () => {
      const rect = contentRef.current?.getBoundingClientRect();

      if (!rect) {
        return;
      }

      setFloatingActionsRect({
        left: rect.left,
        width: rect.width,
      });
    };

    updateFloatingActionsRect();
    window.addEventListener('resize', updateFloatingActionsRect);
    window.visualViewport?.addEventListener('resize', updateFloatingActionsRect);

    const resizeObserver = new ResizeObserver(updateFloatingActionsRect);

    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateFloatingActionsRect);
      window.visualViewport?.removeEventListener('resize', updateFloatingActionsRect);
      resizeObserver.disconnect();
    };
  }, []);

  const toggleSection = (section: SectionKey) => {
    setExpandedSections((current) => ({
      ...current,
      [section]: !current[section],
    }));
  };

  const renderInfoSection = () => (
    <Stack spacing={3}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <RHFTextField variant="filled" name="name" label="Nome" />
        </Grid>

        <Grid item xs={12} md={4}>
          <RHFTextField
            variant="filled"
            name="priority"
            label="Nível de prioridade"
            type="number"
          />
        </Grid>
      </Grid>
      <Controller
        name="visibleForTiers"
        control={hook.control}
        render={({ field, fieldState: { error } }) => {
          const selectedValues = Array.isArray(field.value) ? field.value : [];
          const specificTierValues = hook.TIERS_OPTIONS.filter(
            (option: { value: string }) => option.value !== 'all'
          ).map((option: { value: string }) => option.value);

          const handleTierClick = (clickedValue: string) => {
            const isSelected = selectedValues.includes(clickedValue);

            if (clickedValue === 'all') {
              field.onChange(isSelected ? [] : ['all']);
              return;
            }

            if (isSelected) {
              field.onChange(selectedValues.filter((value: string) => value !== clickedValue));
              return;
            }

            const nextSelectedValues = [
              ...selectedValues.filter((value: string) => value !== 'all'),
              clickedValue,
            ];

            field.onChange(
              specificTierValues.every((value: string) => nextSelectedValues.includes(value))
                ? ['all']
                : nextSelectedValues
            );
          };

          return (
            <Stack spacing={1}>
              <Typography variant="subtitle1" fontWeight={600}>
                Exibir para tiers
              </Typography>

              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {hook.TIERS_OPTIONS.map((option: { value: string; label: string }) => {
                  const isSelected = selectedValues.includes(option.value);

                  return (
                    <Chip
                      key={option.value}
                      label={option.label}
                      clickable
                      onClick={() => handleTierClick(option.value)}
                      color={isSelected ? 'primary' : 'default'}
                      variant={isSelected ? 'filled' : 'outlined'}
                      sx={{
                        fontWeight: 600,
                        color: isSelected ? 'common.white' : 'text.primary',
                        '& .MuiChip-label': {
                          color: isSelected ? 'common.white' : 'inherit',
                        },
                        '&:hover': {
                          backgroundColor: isSelected ? 'primary.main' : 'action.hover',
                          color: isSelected ? 'common.white' : 'text.primary',
                        },
                      }}
                    />
                  );
                })}
              </Stack>

              {error ? (
                <Typography variant="caption" color="error">
                  {error.message}
                </Typography>
              ) : null}
            </Stack>
          );
        }}
      />
    </Stack>
  );

  const renderDisplaySection = () => (
    <Stack spacing={2.5}>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid item xs={12} md={12}>
          <Stack direction='row' alignItems='center' spacing={4}>
            <Stack>
              <Typography variant="body1" fontWeight="bold">
                Tipo de página:
              </Typography>
              <RHFRadioGroupLegacy
                name="type"
                options={hook.TYPE_OPTIONS}
                getOptionLabel={hook.TYPE_LABEL}
              />
            </Stack>
          </Stack>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box
            sx={{
              border: (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.18)}`,
              borderRadius: 1,
              p: 2,
              bgcolor: (theme) => alpha(theme.palette.background.paper, 0.8),
            }}
          >
            <Stack spacing={1.5}>
              <Typography variant="subtitle2" fontWeight={800}>
                Prévia da composição
              </Typography>
              <BannerLayoutPreview type={hook.type} />
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </Stack>
  );

  const renderImagesSection = () => (
    <Stack spacing={2}>
      {hook.fields.map((field: any, index: any) => (
        <Box
          key={field.id}
          sx={{
            border: (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.18)}`,
            borderRadius: 1,
            p: 2,
            bgcolor: (theme) => alpha(theme.palette.background.paper, 0.8),
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                  color: 'common.white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 800,
                  flexShrink: 0,
                }}
              >
                {index + 1}
              </Box>
              <Typography variant="subtitle2" fontWeight={800}>
                Imagem {index + 1}
              </Typography>
            </Stack>

            <RHFUploadSingleFileAccelerator
              name={`images.${index}.url`}
              acceptAll={false}
              accept={{ 'image/*': [] }}
              // @ts-ignore
              onDrop={(files) => hook.handleDrop(files, index)}
            />

            <RHFSelect name={`images.${index}.redirect`} label="Redirecionamento" variant="filled">
              {[{ url: '', name: 'Sem redirecionamento' }, ...redirectOptions].map(
                (option: any) => (
                  <MenuItem key={option.url} value={option.url}>
                    {option.name}
                  </MenuItem>
                )
              )}
            </RHFSelect>

            {hook?.benefitsForNewUsers &&
              hook?.images[index]?.redirect?.includes('forNewUsers') && (
                <Stack spacing={0.5}>
                  <RHFAutocomplete
                    options={hook?.benefitsForNewUsers?.map((b: any) => ({
                      label: `${b.name} - ${b.type === 'product' ? 'Produto' : 'Cupom'}`,
                      value: b.id,
                    }))}
                    name={`images.${index}.benefit`}
                    label="Benefício"
                    variant="filled"
                  />
                  <Typography variant="caption" ml={1} color="text.secondary">
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      Tiers do benefício:
                    </Box>{' '}
                    {hook?.selectedBenefitTiers(index)}
                  </Typography>
                </Stack>
              )}

            <RHFTextField variant="filled" name={`images.${index}.text`} label="Texto" />
          </Stack>
        </Box>
      ))}
    </Stack>
  );

  return (
    <FormProvider methods={hook.methods} onSubmit={hook.onSubmit}>
      <Grid container spacing={3} alignItems="flex-start" pb={8}>
        <Grid item xs={12} lg={10}>
          <Stack ref={contentRef} spacing={3}>
            <Stack direction="row" alignItems="center" justifyContent='flex-end' spacing={1}>
              <Typography variant="subtitle1" fontWeight={600}>
                Ativo
              </Typography>
              <Controller
                name="status"
                control={hook.control}
                render={({ field }) => (
                  <Switch
                    checked={field.value === 'active'}
                    onChange={(_, checked) => field.onChange(checked ? 'active' : 'inactive')}
                  />
                )}
              />
            </Stack>
            <SectionCard
              section="info"
              index={1}
              title="Informações"
              expandedSections={expandedSections}
              onToggleSection={toggleSection}
            >
              {renderInfoSection()}
            </SectionCard>

            <SectionCard
              section="display"
              index={2}
              title="Exibição"
              expandedSections={expandedSections}
              onToggleSection={toggleSection}
            >
              {renderDisplaySection()}
            </SectionCard>

            <SectionCard
              section="images"
              index={3}
              title="Imagens"
              expandedSections={expandedSections}
              onToggleSection={toggleSection}
            >
              {renderImagesSection()}
            </SectionCard>
          </Stack>
        </Grid>
      </Grid>

      <Box
        sx={{
          position: 'fixed',
          left: floatingActionsRect.left,
          width: floatingActionsRect.width,
          bottom: { xs: 'calc(env(safe-area-inset-bottom) + 16px)', sm: 40 },
          zIndex: 10,
          pointerEvents: 'none',
          display: floatingActionsRect.width ? 'block' : 'none',
        }}
      >
        <Stack alignItems={{ xs: 'stretch', sm: 'flex-end' }}>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={hook.isSubmitting}
            disabled={
              (hook.isEdit && !permissions?.update) || (!hook.isEdit && !permissions?.create)
            }
            sx={{
              width: { xs: 1, sm: 'auto' },
              pointerEvents: 'auto',
              boxShadow: (theme) => `0 12px 28px ${alpha(theme.palette.grey[900], 0.16)}`,
            }}
          >
            {!hook.isEdit ? 'Cadastrar Banner' : 'Editar Banner'}
          </LoadingButton>
        </Stack>
      </Box>
    </FormProvider>
  );
}
